import { Input, VStack } from "@chakra-ui/react"
import { Field } from "@hydrologiq/shared/chakra/Field"
import { useState } from "react"
import Form from "@hydrologiq/shared/chakra/custom/Form"
import ErrorAlert from "@hydrologiq/shared/components/ErrorAlert"
import { showSuccessToast, showErrorToast, showLoadingToast, dismissToast } from "@hydrologiq/shared/utils/toast"
import { useMutationWithEndpoint } from "@hydrologiq/shared/hooks/useGraphQLClient"
import {
  CreateSiteDocument,
  type CreateSiteMutation,
  type CreateSiteMutationVariables,
} from "@hydrologiq/gql-experience/graphql"

interface SimpleSiteCreationFormProps {
  customerId: string
  onSuccess: (site: { id: string; name: string }) => void
  onBack?: () => void
}

/**
 * Lightweight site creation form that only requires a name.
 * Does not depend on redux (no location picker).
 * Used within the system creation flow.
 */
const SimpleSiteCreationForm = ({ customerId, onSuccess, onBack }: SimpleSiteCreationFormProps) => {
  const [error, setError] = useState<string | null>(null)
  const [siteName, setSiteName] = useState("")

  const [createSite, { loading }] = useMutationWithEndpoint<CreateSiteMutation, CreateSiteMutationVariables>(
    CreateSiteDocument,
    {
      refetchQueries: ["GetCustomerSites", "SearchCustomers"],
      awaitRefetchQueries: true,
    },
    "primary",
  )

  const handleSubmit = async () => {
    let loadingToastId: string | undefined
    try {
      setError(null)

      const name = siteName.trim()
      if (!name) {
        const errorMessage = "Please provide a site name"
        setError(errorMessage)
        showErrorToast("Validation error", errorMessage)
        return false
      }

      loadingToastId = showLoadingToast("Creating site", "Adding site to customer...")

      const result = await createSite({
        variables: {
          name,
          customer_id: customerId,
        },
      })

      if (result.errors && result.errors.length > 0) {
        let errorMessage = result.errors[0].message || "Failed to create site."

        if (errorMessage.includes("idx_site_tenant_name_active") || errorMessage.includes("duplicate key")) {
          errorMessage = "A site with this name already exists. Please choose a different name."
        }

        setError(errorMessage)
        showErrorToast("Creation failed", errorMessage)
        return false
      }

      if (result.data?.createSite) {
        const site = result.data.createSite
        showSuccessToast("Site created", `Site "${name}" has been created successfully.`)
        setError(null)
        setSiteName("")
        onSuccess({ id: site.id, name: site.name })
        return true
      } else {
        const errorMessage = "Failed to create site. Unexpected response."
        setError(errorMessage)
        showErrorToast("Creation failed", errorMessage)
        return false
      }
    } catch (err) {
      let errorMessage = err instanceof Error ? err.message : "An unexpected error occurred"

      if (errorMessage.includes("idx_site_tenant_name_active") || errorMessage.includes("duplicate key")) {
        errorMessage = "A site with this name already exists. Please choose a different name."
      }

      setError(errorMessage)
      showErrorToast("Creation failed", errorMessage)
      return false
    } finally {
      if (loadingToastId) dismissToast(loadingToastId)
    }
  }

  const handleBack = () => {
    setError(null)
    setSiteName("")
    if (onBack) onBack()
  }

  return (
    <Form
      heading="Create a site"
      helperText="Please provide site information below."
      actionType="create"
      actionContent={{ label: "Create", description: "Create the site" }}
      loading={loading}
      onSubmit={handleSubmit}
      onBack={onBack ? handleBack : undefined}
    >
      <VStack gap={5}>
        {error && <ErrorAlert title="Site Creation Error" message={error} onClose={() => setError(null)} />}
        <Field label="Site name" required>
          <Input
            name="name"
            value={siteName}
            onChange={(e) => setSiteName(e.target.value)}
            placeholder="Enter site name"
          />
        </Field>
      </VStack>
    </Form>
  )
}

export default SimpleSiteCreationForm
